import EventEmitter from 'eventemitter3';
import lodashMerge from 'lodash/merge';
import queryString from 'query-string';

import {assertDependencies, inherits} from '../lib/util';

const STATUS_STRINGS = {
	'true': 'ENABLED',
	'false': 'DISABLED'
};

export const EMIT = {
	entered_scene: 'entered_scene'
};

const MANIFEST = require('./manifest');

const DEFAULT_OPTIONS = {
	enabled: true,

	// How often to check for pano change
	update_interval: 350,

	// Update browser URL query to match the current scene
	sync_query_string: true
};

function SceneObserver(options = DEFAULT_OPTIONS, deps = MANIFEST.dependencies) {
	const thisSceneObserver = this;
	EventEmitter.call(thisSceneObserver);

	options = lodashMerge({}, DEFAULT_OPTIONS, options);
	assertDependencies(MANIFEST.name, MANIFEST.dependencies, deps);

	const log = deps.logger.child(MANIFEST.name);
	const pushState = deps.history && deps.history.pushState && deps.history.pushState.bind(deps.history);

	/** @type {SceneInfo} */
	let _currentScene = null;
	let _journeyStep = 0;
	let _enabled = options.enabled;
	let _timeout = null;

	Object.assign(thisSceneObserver, /** @lends SceneObserver.prototype */ {
		init,
		isEnabled: () => _enabled,
		getCurrentScene: () => _currentScene,
		getCurrentSceneName: () => (_currentScene && _currentScene.name) || null,
		getJourneyStep: () => _journeyStep,
		enable: () => setEnabled(true),
		disable: () => setEnabled(false),
	});

	function init() {
		runUpdateTimer();
		update();
		log.verbose('Initialized');
	}

	function setEnabled(val) {
		if (val !== _enabled) {
			_enabled = val;
			log.info(`Observer has been ${STATUS_STRINGS[_enabled]}`);
		}
	}

	function runUpdateTimer() {
		clearTimeout(_timeout);
		_timeout = setTimeout(() => {
			update();
			runUpdateTimer();
		}, options.update_interval);
	}

	function update() {
		if (!options.enabled || !deps.krPanoClient || !deps.krPanoClient.isReady()) {
			return;
		}

		const sceneName = deps.krPanoClient.getCurrentSceneName();
		if (!sceneName || (_currentScene && _currentScene.name === sceneName)) {
			return;
		}

		_journeyStep++;
		const prevScene = _currentScene;
		_currentScene = deps.krPanoClient.getSceneInfo(sceneName);

		if (options.sync_query_string && pushState && deps.location) {
			const url = sceneToUrl(_currentScene);
			const pageTitle = sceneToPageTitle(_currentScene);
			pushState(url, pageTitle, url);
		}

		thisSceneObserver.emit(EMIT.entered_scene, _currentScene, prevScene, _journeyStep);

		if (prevScene) {
			log.verbose(`Transitioned from ${prevScene.name} to ${_currentScene.name} (step ${_journeyStep})`);
		} else {
			log.verbose(`Entered scene ${_currentScene.name}`);
		}
	}

	/**
	 * @param {SceneInfo} scene
	 */
	function sceneToUrl(scene) {
		const qs = queryString.parse(deps.location.search);
		qs.s = scene.name;
		return '?' + queryString.stringify(qs);
	}

	/**
	 * @param {SceneInfo} scene
	 */
	function sceneToPageTitle(scene) {
		return scene.title;
	}
}
inherits(SceneObserver, EventEmitter);

// *********************************************************************************************************************

export default SceneObserver;
